/**
 * CE QUE PARTAGENT LES ONGLETS SECTEURS, FAMILLES, SOUS-FAMILLES ET SAISONS des Référentiels — le calcul des rangs à
 * réécrire, le bloc des valeurs « hors de la liste » et le bandeau d'erreur.
 *
 * ⚠️ UN SEUL CALCUL DES RANGS : les flèches des secteurs et des saisons, le glisser des familles et l'ajout en tête ou
 *    en fin écrivent tous `sortOrder` par lui. Trois copies rangeraient différemment la même liste.
 */
import { useState } from 'react';
import { t } from '../i18n';
import type { ReferentialFacetOrphan } from '../api';

/** Une ligne telle que les rangs la connaissent : son code et son rang posé (0 = jamais rangée). */
export interface Ranked {
  code: string;
  sortOrder: number;
}

/**
 * LES RANGS À RÉÉCRIRE pour que la liste suive `order` (des codes) : chaque ligne prend sa position, à partir de 1, et
 * seules celles dont le rang change sont écrites.
 * ⚠️ UNE LISTE DONT CERTAINES LIGNES N'ONT JAMAIS ÉTÉ RANGÉES (0, venues de l'EDI ou d'un import) est entièrement
 *    numérotée au premier geste : sans cela, échanger deux lignes à 0 ne changerait rien, et la flèche ferait semblant.
 */
export function rankWrites(rows: readonly Ranked[], order: readonly string[], offset = 0): { code: string; sortOrder: number }[] {
  const current = new Map(rows.map((r) => [r.code, r.sortOrder]));
  return order.flatMap((code, i) => (current.get(code) === i + 1 + offset ? [] : [{ code, sortOrder: i + 1 + offset }]));
}

/** L'ordre après avoir échangé la ligne `i` avec sa voisine `i + step` — inchangé aux bords. */
export function swapped(order: readonly string[], i: number, step: -1 | 1): string[] {
  const j = i + step;
  if (j < 0 || j >= order.length) return [...order];
  const next = [...order];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

/** Le motif réel d'un échec, tel que le client d'API le rend. */
export const reasonOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** Le bandeau rose sous la ligne d'ajout — celui de la maquette, pour un doublon comme pour un refus du serveur. */
export function FacetError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div role="alert" style={{
      padding: '10px 18px', borderBottom: '1px solid var(--ink-100)', background: 'var(--rose-bg)',
      fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--rose-fg)',
    }}>{message}</div>
  );
}

/** Une liste qui n'a pas pu être lue — ce n'est pas une liste vide, et l'écran le dit. */
export function FacetReadFailure({ failure }: { failure: string }) {
  return (
    <p role="alert" style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--danger-ink)', lineHeight: 1.55 }}>
      {t('Le référentiel n’a pas pu être lu : {failure}. Ce n’est pas une liste vide.', { failure })}
    </p>
  );
}

/** Au-delà, le bloc se replie : une reprise peut apporter des centaines de saisons hors liste. */
const ORPHANS_SHOWN = 8;

/**
 * LES VALEURS HORS DE LA LISTE — portées par des articles en service, qu'aucune ligne du référentiel ne désigne.
 * ⚠️ LES TAIRE FERAIT CROIRE QUE LA LISTE DIT TOUT CE QUE LE CATALOGUE EMPLOIE : une saison reprise en `MMAAAA`, une
 *    famille saisie avant que la liste existe. « Ajouter à la liste » crée la ligne sous ce libellé : les articles qui
 *    le portent s'y rattachent d'eux-mêmes (par le libellé normalisé).
 */
export function FacetOrphans({ orphans, mono, busy, onAdd }: {
  orphans: readonly ReferentialFacetOrphan[];
  mono?: boolean;
  busy: boolean;
  onAdd: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (orphans.length === 0) return null;
  const shown = open ? orphans : orphans.slice(0, ORPHANS_SHOWN);
  return (
    <div style={{ marginTop: 16, maxWidth: 640, background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 130, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>
          {t('Hors de la liste')}</span>
        <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
          {t('{n} valeur portée par des articles, que la liste ne connaît pas', { n: orphans.length })}</span>
      </div>
      {shown.map((o) => (
        <div key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 18px', minHeight: 46, borderBottom: '1px solid var(--ink-100)' }}>
          <span style={{
            flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ink-900)',
            ...(mono ? { fontFamily: 'var(--font-mono)', fontSize: 'var(--t-lead)', fontWeight: 700 } : { fontSize: 'var(--t-lead)', fontWeight: 600 }),
          }}>{o.value}</span>
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
            {t('{n} article', { n: o.models })}</span>
          <button type="button" disabled={busy} onClick={() => onAdd(o.value)} className="oz-ref-ghost"
            style={{
              height: 30, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
              color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap', cursor: busy ? 'default' : 'pointer',
            }}>{t('Ajouter à la liste')}</button>
        </div>
      ))}
      {orphans.length > ORPHANS_SHOWN && (
        <button type="button" onClick={() => setOpen((v) => !v)} className="oz-ref-ghost"
          style={{
            display: 'block', width: '100%', padding: '11px 18px', border: 'none', background: 'var(--ink-50)', textAlign: 'left',
            fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-600)', cursor: 'pointer',
          }}>
          {open ? t('Replier la liste') : t('Afficher les {n} autres', { n: orphans.length - ORPHANS_SHOWN })}
        </button>
      )}
    </div>
  );
}
